import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from './contexts/ThemeContext'
import { LanguageProvider } from './contexts/LanguageContext'
import App from './App'
import './index.css'

// Helper function to check if error is from browser extension
const isExtensionError = (errorMessage: string, errorName: string, fileName: string, stack: string): boolean => {
  const msg = (errorMessage || '').toString();
  const name = (errorName || '').toString();
  const file = (fileName || '').toString();
  const stk = (stack || '').toString();
  
  return (
    msg.includes('message channel closed') ||
    msg.includes('asynchronous response') ||
    msg.includes('Extension context invalidated') ||
    msg.includes('Receiving end does not exist') ||
    msg.includes('codes.forEach') ||
    msg.includes('getBestMatchFromCodes') ||
    msg.includes('content_script.js') ||
    file.includes('content_script.js') ||
    stk.includes('content_script.js') ||
    stk.includes('getBestMatchFromCodes') ||
    file.includes('chrome-extension://') ||
    msg.includes('chrome-extension://') ||
    (name === 'TypeError' && (msg.includes('forEach') || stk.includes('getBestMatchFromCodes'))) ||
    (name === 'Error' && msg.includes('message channel')) ||
    (msg.includes('WebSocket connection to') && msg.includes('localhost:8081'))
  );
};

// Suppress browser extension errors that are harmless
const suppressExtensionErrors = () => {
  // Function to check if any argument contains extension error patterns
  const shouldSuppressConsoleOutput = (args: any[]): boolean => {
    let fullString = '';
    
    // Convert all arguments to string for pattern matching
    for (const arg of args) {
      if (arg && typeof arg === 'object') {
        const msg = arg.message || arg.toString() || '';
        const name = arg.name || '';
        const stack = arg.stack || '';
        fullString += msg + ' ' + name + ' ' + stack + ' ';
      } else {
        fullString += String(arg) + ' ';
      }
    }
    
    // Check the full concatenated string for extension patterns
    return isExtensionError(fullString, '', '', '') || 
           fullString.includes('content_script.js:') ||
           fullString.includes('getBestMatchFromCodes') ||
           (fullString.includes('TypeError') && fullString.includes('codes.forEach'));
  };
  
  // Override console methods to filter extension errors
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleLog = console.log;
  
  console.error = (...args: any[]) => {
    if (!shouldSuppressConsoleOutput(args)) {
      originalConsoleError.apply(console, args);
    }
  };
  
  console.warn = (...args: any[]) => {
    if (!shouldSuppressConsoleOutput(args)) {
      originalConsoleWarn.apply(console, args);
    }
  };
  
  // Also intercept console.log for "Uncaught" messages
  console.log = (...args: any[]) => {
    const fullString = args.join(' ');
    if (!shouldSuppressConsoleOutput(args) && !fullString.includes('Uncaught')) {
      originalConsoleLog.apply(console, args);
    }
  };
  
  // Handle unhandled promise rejections (common with browser extensions)
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';
    const errorName = event.reason?.name || '';
    const stack = event.reason?.stack || '';
    
    // Also check the full error object string representation
    const fullErrorString = errorMessage + ' ' + stack;
    
    if (isExtensionError(errorMessage, errorName, '', stack) || 
        fullErrorString.includes('content_script.js') ||
        fullErrorString.includes('getBestMatchFromCodes') ||
        (errorName === 'TypeError' && fullErrorString.includes('codes.forEach'))) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);

  // Handle general errors (catch any remaining extension-related errors)
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || event.error?.message || '';
    const errorName = event.error?.name || '';
    const fileName = event.filename || '';
    const stack = event.error?.stack || '';
    
    if (isExtensionError(errorMessage, errorName, fileName, stack)) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
};

// Initialize error suppression
suppressExtensionErrors();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </ThemeProvider>
  </StrictMode>,
)
